import dayjs, { type ConfigType } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/* eslint-disable @typescript-eslint/no-explicit-any */
export function groupByKey<T extends Record<string, unknown>, K extends keyof T>(
  data: T[],
  key: K
): Record<string, T> {
  return data.reduce<Record<string, T>>((acc, curr) => {
    const groupKey = String(curr[key]);
    acc[groupKey] = curr;
    return acc;
  }, {});
}

export const getPercentage = (value: number, total: number): number => {
  return (value / total) * 100;
};

export function arrayToObject<
  T extends Record<K | V, unknown>,
  K extends keyof T,
  V extends keyof T,
  TK extends T[K] & PropertyKey,
>(array: T[], key: K, value: V): Record<TK, T[V]> {
  return (array ?? []).reduce(
    (acc, item) => {
      acc[item[key] as TK] = item[value];
      return acc;
    },
    {} as Record<TK, T[V]>
  );
}
export const formatCurrency = (
  value: number | string,
  currency: string = 'IDR',
  locale: string = 'id-ID'
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
};

export const formatThousands = (value: number | string, locale: string = 'id-ID'): string => {
  return Number(value)?.toLocaleString(locale) || '0';
};

export const convertToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export async function imageUrlToBase64(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const isValidTime = (value: ConfigType) => {
  return dayjs(value, 'HH:mm:ss').isValid();
};

export const formatTime = (time: ConfigType, format = 'HH:mm') => {
  if (isValidTime(time)) {
    const result = dayjs(time, 'HH:mm:ss');
    return result.format(format);
  }
  return '-';
};

export function removeEmptyValues<T extends Record<string, any>>(obj: T): Partial<T> {
  const entries = Object.entries(obj).filter(([_, value]) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  });

  return Object.fromEntries(entries) as Partial<T>;
}

export function isChrome(): boolean {
  const ua = navigator.userAgent;
  const platform = navigator.platform || 'unknown';

  // Normalize UA for easier matching
  // const isMobile = /Android|iPhone|iPad|iPod/i.test(platform);
  const isDesktop = /Win|Mac|Linux/i.test(platform);

  // Exclude known Chrome-like browsers
  const isExcluded = /MiuiBrowser|MiBrowser|Edg|OPR|Brave|SamsungBrowser|UCBrowser|Vivaldi/i.test(
    ua
  );

  // Detect Chrome on iOS (CriOS)
  const isChromeIOS = /CriOS/.test(ua) && /iPhone|iPad|iPod/.test(platform);

  // Detect Chrome on Android
  const isChromeAndroid = /Android/.test(ua) && /Chrome\/[0-9.]+/.test(ua) && !isExcluded;

  // Detect Chrome on Desktop
  const isChromeDesktop = /Chrome\/[0-9.]+/.test(ua) && isDesktop && !isExcluded;

  return isChromeIOS || isChromeAndroid || isChromeDesktop;
}

type IsWithinRadius = {
  currentLocation: {
    lat: number;
    lng: number;
  };
  targets: {
    lat: number;
    lng: number;
    radiusMeters: number;
  }[];
};

export function isWithinRadius({ currentLocation, targets }: IsWithinRadius): boolean {
  if (!currentLocation || !targets?.length) return false;

  const R = 6371000; // Earth radius in meters
  const toRad = (val: number) => (val * Math.PI) / 180;
  const { lat: lat1, lng: lng1 } = currentLocation;

  return targets.some(({ lat: lat2, lng: lng2, radiusMeters }) => {
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lng2 - lng1);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance <= radiusMeters;
  });
}
